import { useCallback, useContext, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import ModalContext from 'contexts/ModalContext'
import { getCustomerLanguagePreferenceFromAuth, updateCustomerLanguagePreferenceFromAuth } from 'api/auth'
import { syncUserDetails } from 'actions/AuthActions'
import { SET_REQUEST_LOCALE } from 'actions/actionConstants'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getLocaleFromPathname, replaceLocaleInUrl, isSupportedLocale } from 'i18n/util/localeUrl'
import type { SupportedLocale } from 'i18n/supportedLocales'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'
import { reportClientError, reportClientMessage } from 'services/errorReportingService'
import LocaleRedirectModal from 'components/Common/Modals/LocaleRedirectModal/LocaleRedirectModal'

type ConflictChoice = 'url' | 'auth'

function getConflictSessionKey(memberId: string, urlLocale: string, authLocale: string): string {
  return `auth-backed-locale-conflict:${memberId}:${urlLocale}:${authLocale}`
}

function reportUnsupportedAuthPreference(memberId: string, language: string) {
  reportClientMessage(
    'auth_language_preference_unsupported',
    { memberId, language },
    'warning',
    { domain: 'i18n', surface: 'auth_language_preference' },
  )
}

/**
 * Reconciles the authenticated customer language preference with the URL locale
 * after hydration. Clean URLs stay clean for auth-only preferences, explicit URL
 * locales remain the active page-language signal, URL-only preferences are
 * written back to auth, and conflicts are resolved through a one-per-session
 * confirmation modal.
 */
export default function AuthBackedLocalePreferenceSync() {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const accountLanguage = useAppSelector(state => state.auth.account.language)

  const navigateToLocale = useCallback((locale: SupportedLocale) => {
    dispatch({ type: SET_REQUEST_LOCALE, locale })

    const currentLocationUrl = `${location.pathname}${location.search}${location.hash}`
    const targetUrl = replaceLocaleInUrl(currentLocationUrl, locale)
    if (targetUrl !== currentLocationUrl) {
      // Force a full navigation so app bootstrap, SSR data, and locale-aware routing re-run for the URL locale.
      window.location.assign(targetUrl)
    }
  }, [dispatch, location])

  const writeAccountLocale = useCallback(async(locale: SupportedLocale, signal?: AbortSignal) => {
    try {
      await updateCustomerLanguagePreferenceFromAuth(locale, signal)
      if (signal?.aborted) return

      dispatch(syncUserDetails({ language: locale }))
    } catch(error) {
      reportClientError(error)
    }
  }, [dispatch])

  useEffect(() => {
    if (!isLoggedIn || !memberId) return

    const authenticatedMemberId = memberId
    const abortController = new AbortController()
    const { signal } = abortController

    // Redux auth state can be hydrated from stale SSR/session data, so conflict
    // checks need a fresh preference read before prompting or writing back.
    async function getSupportedAuthLocalePreference(): Promise<SupportedLocale | null | undefined> {
      let authPreference: string | null
      try {
        authPreference = await getCustomerLanguagePreferenceFromAuth(signal)
      } catch(error) {
        reportClientError(error)
        return undefined
      }

      if (authPreference && !isSupportedLocale(authPreference)) {
        reportUnsupportedAuthPreference(authenticatedMemberId, authPreference)
        return undefined
      }

      return authPreference as SupportedLocale | null
    }

    const urlLocale = getLocaleFromPathname(location.pathname)
    const shouldFetchAuthPreference = accountLanguage === undefined
    const authPreferencePromise = shouldFetchAuthPreference ?
        getSupportedAuthLocalePreference() :
        Promise.resolve(accountLanguage)

    authPreferencePromise.then(async(authPreference) => {
      if (signal.aborted) return

      if (authPreference === undefined) {
        return
      }

      if (!shouldFetchAuthPreference && authPreference && !isSupportedLocale(authPreference)) {
        reportUnsupportedAuthPreference(authenticatedMemberId, authPreference)
        return
      }

      let authLocale = authPreference as SupportedLocale | null
      if (!urlLocale) {
        if (authLocale) {
          dispatch({ type: SET_REQUEST_LOCALE, locale: authLocale })
        }
        return
      }

      if (!isSupportedLocale(urlLocale)) return

      if (!authLocale) {
        await writeAccountLocale(urlLocale, signal)
        if (signal.aborted) return

        dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
        return
      }

      if (urlLocale === authLocale) {
        dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
        return
      }

      if (!shouldFetchAuthPreference) {
        const freshAuthLocale = await getSupportedAuthLocalePreference()
        if (signal.aborted || freshAuthLocale === undefined) return

        if (!freshAuthLocale) {
          await writeAccountLocale(urlLocale, signal)
          if (signal.aborted) return
          dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
          return
        }

        // prevent stale Redux auth store from triggering conflict modal
        // when user just changed language through navbar
        if (urlLocale === freshAuthLocale) {
          dispatch(syncUserDetails({ language: freshAuthLocale }))
          dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
          return
        }

        authLocale = freshAuthLocale
      }

      const conflictSessionKey = getConflictSessionKey(authenticatedMemberId, urlLocale, authLocale)
      if (isomorphicSessionStorage.get(conflictSessionKey)) return
      isomorphicSessionStorage.set(conflictSessionKey, true)

      let choice: ConflictChoice | undefined
      try {
        choice = await showModal<ConflictChoice>(<LocaleRedirectModal urlLocale={urlLocale} authLocale={authLocale} />)
      } catch(error) {
        reportClientError(error)
        return
      }

      if (signal.aborted) return

      if (choice === 'url') {
        await writeAccountLocale(urlLocale, signal)
        if (signal.aborted) return
        dispatch({ type: SET_REQUEST_LOCALE, locale: urlLocale })
      } else if (choice === 'auth') {
        navigateToLocale(authLocale)
      }
    })

    return () => {
      abortController.abort()
    }
  }, [accountLanguage, dispatch, isLoggedIn, location.pathname, memberId, navigateToLocale, showModal, writeAccountLocale])

  return null
}
